import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { STATUS_META } from "./constants";
import { getAllUsers } from "../../../api/users.api";
import { generateSlots } from "./utils";

const BookingModal = ({
  open,
  onClose,
  onSave,
  onDelete,
  booking,
  leadName,
  type,
  durations = [],
  workingHours,
  globalBookedSlots,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  // Colors consistent with Reports_v2
  const colors = {
    success: "#10B981",
    successLight: "#ECFDF5",
    warning: "#F59E0B",
    warningLight: "#FFFBEB",
    danger: "#EF4444",
    dangerLight: "#FEF2F2",
    bgPrimary: "#F8FAFC",
    bgCard: "#FFFFFF",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    textMuted: "#94A3B8",
    border: "#E2E8F0",
  };

  const [users, setUsers] = useState([]);
  const [consultant, setConsultant] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [notes, setNotes] = useState("");

  // Fixed duration - always 60 minutes (1 hour)
  const duration = 60;

  const isNew = !booking?._id || booking?.isNew;

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getAllUsers();
        setUsers(res.data.data || []);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      }
    };
    fetchUsers();
  }, []);

  // Initialize form when booking changes
  useEffect(() => {
    if (booking) {
      const bookingDate = booking?.date
        ? new Date(booking.date).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);

      setConsultant(booking?.consultant?._id || booking?.consultant || "");
      setStatus(booking?.status || "Scheduled");
      setDate(bookingDate);
      setSlot(booking?.slot || "");
      setNotes(booking?.internal_comments || "");
    }
  }, [booking, open]);

  const slots = generateSlots(duration, workingHours);

  const statuses = [
    {
      value: "Scheduled",
      label: "SCHEDULED",
      icon: STATUS_META.Scheduled.icon,
    },
    {
      value: "Confirmed",
      label: "CONFIRMED",
      icon: STATUS_META.Confirmed.icon,
    },
    { value: "Ongoing", label: "ONGOING", icon: STATUS_META.Ongoing.icon },
    {
      value: "Completed",
      label: "COMPLETED",
      icon: STATUS_META.Completed.icon,
    },
    {
      value: "Cancelled",
      label: "CANCELLED",
      icon: STATUS_META.Cancelled.icon,
    },
  ];

  const handleSave = () => {
    if (!date || !slot) return;

    onSave?.({
      status,
      date: new Date(date).toISOString(),
      slot,
      duration,
      consultant: consultant || null,
      internal_comments: notes,
    });
    // Close modal after successful save
    onClose();
  };

  const handleDelete = () => {
    if (!window.confirm("Delete this booking?")) return;
    onDelete?.();
    // Close modal after delete
    onClose();
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
  };

  const getStatusChipStyle = (statusValue) => {
    const baseStyle = {
      height: 38,
      fontSize: 11,
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.2s ease",
      borderRadius: 2.5,
      px: 1,
      minWidth: 95,
      "& .MuiChip-label": {
        px: 2,
        justifyContent: "center",
      },
    };

    const statusColors = {
      Scheduled: { bg: alpha(primaryColor, 0.12), color: primaryColor },
      Confirmed: { bg: colors.successLight, color: colors.success },
      Ongoing: { bg: colors.warningLight, color: colors.warning },
      Completed: { bg: colors.successLight, color: colors.success },
      Cancelled: { bg: colors.dangerLight, color: colors.danger },
    };

    if (status === statusValue) {
      const selectedColor = statusColors[statusValue] || statusColors.Scheduled;
      return {
        ...baseStyle,
        bgcolor: selectedColor.bg,
        color: selectedColor.color,
        border: "none",
        boxShadow: `0 2px 8px ${alpha(selectedColor.color, 0.2)}`,
        "&:hover": {
          bgcolor: selectedColor.bg,
          opacity: 0.9,
        },
      };
    }

    return {
      ...baseStyle,
      bgcolor: colors.bgCard,
      color: colors.textMuted,
      border: `1px solid ${colors.border}`,
      boxShadow: "none",
      "&:hover": {
        bgcolor: colors.bgPrimary,
        borderColor: colors.textMuted,
      },
    };
  };

  const formFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      fontSize: 14,
      bgcolor: colors.bgCard,
      "& fieldset": {
        borderColor: colors.border,
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: alpha(primaryColor, 0.5),
      },
      "&.Mui-focused fieldset": {
        borderColor: primaryColor,
        borderWidth: 1.5,
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
            maxWidth: 480,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  mb: 0.5,
                  letterSpacing: -0.3,
                }}
              >
                {isNew ? "New Appointment" : "Edit Appointment"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: colors.textSecondary,
                  lineHeight: 1.4,
                }}
              >
                {isNew ? "Create" : "Update"} Booking For{" "}
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, color: colors.textPrimary }}
                >
                  {leadName || "This Lead"}
                </Typography>
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                width: 36,
                height: 36,
                color: colors.textMuted,
                bgcolor: alpha(colors.textMuted, 0.08),
                "&:hover": {
                  bgcolor: alpha(colors.textMuted, 0.15),
                  color: colors.textSecondary,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Date */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Date
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <CalendarTodayOutlinedIcon
                      sx={{ fontSize: 16, color: colors.textMuted, mr: 1 }}
                    />
                  ),
                  sx: { height: 42 },
                }}
                sx={formFieldSx}
              />
            </Box>

            {/* Time Slot */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Time Slot (1 Hour)
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={slot}
                  onChange={(e) => setSlot(e.target.value)}
                  displayEmpty
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{
                    ...formFieldSx["& .MuiOutlinedInput-root"],
                    height: 42,
                    bgcolor: colors.bgCard,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.border,
                      borderWidth: 1,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(primaryColor, 0.5),
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                      borderWidth: 1.5,
                    },
                  }}
                >
                  <MenuItem value="">
                    <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                      Select Time Slot
                    </Typography>
                  </MenuItem>
                  {slots.map((s) => {
                    const key = `${date}|${s}|${type}`;
                    const existingCount = globalBookedSlots?.[key] || 0;
                    const bookingCount = existingCount + 1; // +1 for current booking

                    return (
                      <MenuItem key={s} value={s}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: colors.textPrimary,
                              fontWeight: 500,
                            }}
                          >
                            {s}
                          </Typography>
                          {existingCount > 0 ? (
                            <Chip
                              label={`${bookingCount} Bookings`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: alpha(primaryColor, 0.1),
                                color: primaryColor,
                                borderRadius: 1.5,
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: colors.success,
                                fontWeight: 600,
                                bgcolor: colors.successLight,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                              }}
                            >
                              Available
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>

            {/* Consultant */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Consultant
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={consultant}
                  onChange={(e) => setConsultant(e.target.value)}
                  displayEmpty
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{
                    ...formFieldSx["& .MuiOutlinedInput-root"],
                    height: 42,
                    bgcolor: colors.bgCard,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.border,
                      borderWidth: 1,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(primaryColor, 0.5),
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                      borderWidth: 1.5,
                    },
                  }}
                >
                  <MenuItem value="">
                    <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                      Select Consultant (Optional)
                    </Typography>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PersonIcon
                          sx={{ fontSize: 16, color: colors.textMuted }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: colors.textPrimary,
                            fontWeight: 500,
                          }}
                        >
                          {user.name}
                        </Typography>
                        {user.team && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: colors.textMuted,
                              ml: 0.5,
                            }}
                          >
                            ({user.team})
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ borderStyle: "dashed", my: 1 }} />

            {/* Status */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 2,
                }}
              >
                Status
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "flex-start",
                }}
              >
                {statuses.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Chip
                      key={s.value}
                      icon={<Icon sx={{ fontSize: 14 }} />}
                      label={s.label}
                      onClick={() => handleStatusChange(s.value)}
                      sx={getStatusChipStyle(s.value)}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Internal Notes */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Internal Notes
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Add Any Internal Notes Or Special Instructions..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{
                  ...formFieldSx,
                  "& .MuiOutlinedInput-input": {
                    fontSize: 14,
                    py: 1.5,
                    px: 2,
                  },
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            p: 2.5,
            borderTop: `1px solid ${colors.border}`,
            bgcolor: alpha(colors.bgPrimary, 0.5),
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            {!isNew && (
              <Button
                onClick={handleDelete}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  color: colors.danger,
                  px: 2,
                  py: 1,
                  borderRadius: 2.5,
                  "&:hover": {
                    bgcolor: colors.dangerLight,
                  },
                }}
              >
                Delete
              </Button>
            )}
            {isNew && <Box sx={{ flex: 1 }} />}

            <Stack direction="row" spacing={1.5}>
              <Button
                onClick={onClose}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  color: colors.textSecondary,
                  px: 2.5,
                  py: 1,
                  borderRadius: 2.5,
                  "&:hover": {
                    bgcolor: alpha(colors.textMuted, 0.08),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                disabled={!date || !slot}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  bgcolor: primaryColor,
                  color: "#FFF",
                  px: 3,
                  py: 1,
                  borderRadius: 2.5,
                  boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
                  "&:hover": {
                    bgcolor: primaryColor,
                    boxShadow: `0 4px 12px ${alpha(primaryColor, 0.4)}`,
                  },
                  "&:disabled": {
                    bgcolor: colors.border,
                    color: colors.textMuted,
                    boxShadow: "none",
                  },
                }}
              >
                {isNew ? "Create Booking" : "Save Changes"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
